import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PerformanceAnalytics = () => {
  const partnerPerformance = [
    {
      id: 1,
      name: 'Global Airport Services',
      category: 'Airport Services',
      bookings: 1247,
      successRate: 98.5,
      satisfaction: 4.8,
      avgCost: 85,
      responseTime: 15,
      trend: 'up'
    },
    {
      id: 2,
      name: 'SecureTravel Protection',
      category: 'Security',
      bookings: 856,
      successRate: 99.2,
      satisfaction: 4.9,
      avgCost: 350,
      responseTime: 5,
      trend: 'up'
    },
    {
      id: 3,
      name: 'Elite Dining Network',
      category: 'Restaurant',
      bookings: 2134,
      successRate: 96.8,
      satisfaction: 4.7,
      avgCost: 175,
      responseTime: 30,
      trend: 'down'
    },
    {
      id: 4,
      name: 'MediCare International',
      category: 'Healthcare',
      bookings: 645,
      successRate: 99.8,
      satisfaction: 4.9,
      avgCost: 250,
      responseTime: 10,
      trend: 'up'
    },
    {
      id: 5,
      name: 'Premium Airport Concierge',
      category: 'Airport Services',
      bookings: 423,
      successRate: 94.2,
      satisfaction: 4.5,
      avgCost: 55,
      responseTime: 20,
      trend: 'down'
    }
  ];

  const costAnalysis = [
    { category: 'Airport Services', spent: 125400, bookings: 1670, avgCost: 75 },
    { category: 'Security', spent: 299600, bookings: 856, avgCost: 350 },
    { category: 'Restaurant', spent: 373450, bookings: 2134, avgCost: 175 },
    { category: 'Healthcare', spent: 161250, bookings: 645, avgCost: 250 }
  ];

  const getTrendIcon = (trend) => {
    return trend === 'up' ? 'TrendingUp' : 'TrendingDown';
  };

  const getTrendColor = (trend) => {
    return trend === 'up' ? 'text-success' : 'text-error';
  };

  const getPerformanceColor = (value, threshold) => {
    if (value >= threshold) return 'text-success';
    if (value >= threshold * 0.9) return 'text-warning';
    return 'text-error';
  };

  return (
    <div className="space-y-6">
      {/* Analytics Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="glass-container rounded-xl border border-border/50 p-6">
          <div className="flex items-center justify-between mb-2">
            <Icon name="TrendingUp" size={24} className="text-success" />
            <span className="text-2xl font-bold text-foreground">97.8%</span>
          </div>
          <p className="text-sm text-muted-foreground">Avg Success Rate</p>
        </div>
        <div className="glass-container rounded-xl border border-border/50 p-6">
          <div className="flex items-center justify-between mb-2">
            <Icon name="Star" size={24} className="text-warning" />
            <span className="text-2xl font-bold text-foreground">4.76</span>
          </div>
          <p className="text-sm text-muted-foreground">Avg Satisfaction</p>
        </div>
        <div className="glass-container rounded-xl border border-border/50 p-6">
          <div className="flex items-center justify-between mb-2">
            <Icon name="DollarSign" size={24} className="text-accent" />
            <span className="text-2xl font-bold text-foreground">$959K</span>
          </div>
          <p className="text-sm text-muted-foreground">Total Spent</p>
        </div>
        <div className="glass-container rounded-xl border border-border/50 p-6">
          <div className="flex items-center justify-between mb-2">
            <Icon name="Clock" size={24} className="text-primary" />
            <span className="text-2xl font-bold text-foreground">16min</span>
          </div>
          <p className="text-sm text-muted-foreground">Avg Response</p>
        </div>
      </div>
      {/* Partner Performance Table */}
      <div className="glass-container rounded-xl border border-border/50">
        <div className="p-6 border-b border-border/50">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-foreground">Partner Performance Metrics</h3>
              <p className="text-sm text-muted-foreground mt-1">Comprehensive performance analysis</p>
            </div>
            <Button variant="outline" size="sm" iconName="Download">
              Export Report
            </Button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border/30">
                <th className="text-left p-4 text-sm font-semibold text-muted-foreground">Partner</th>
                <th className="text-left p-4 text-sm font-semibold text-muted-foreground">Category</th>
                <th className="text-center p-4 text-sm font-semibold text-muted-foreground">Bookings</th>
                <th className="text-center p-4 text-sm font-semibold text-muted-foreground">Success Rate</th>
                <th className="text-center p-4 text-sm font-semibold text-muted-foreground">Satisfaction</th>
                <th className="text-center p-4 text-sm font-semibold text-muted-foreground">Avg Cost</th>
                <th className="text-center p-4 text-sm font-semibold text-muted-foreground">Response</th>
                <th className="text-center p-4 text-sm font-semibold text-muted-foreground">Trend</th>
              </tr>
            </thead>
            <tbody>
              {partnerPerformance?.map((partner) => (
                <tr key={partner?.id} className="border-b border-border/30 hover:bg-white/5 transition-colors">
                  <td className="p-4">
                    <div className="font-medium text-foreground">{partner?.name}</div>
                  </td>
                  <td className="p-4">
                    <span className="text-sm text-muted-foreground">{partner?.category}</span>
                  </td>
                  <td className="p-4 text-center">
                    <span className="text-sm font-medium text-foreground">{partner?.bookings?.toLocaleString()}</span>
                  </td>
                  <td className="p-4 text-center">
                    <span className={`text-sm font-medium ${getPerformanceColor(partner?.successRate, 95)}`}>
                      {partner?.successRate}%
                    </span>
                  </td>
                  <td className="p-4 text-center">
                    <div className="flex items-center justify-center space-x-1">
                      <Icon name="Star" size={14} className="text-warning" />
                      <span className={`text-sm font-medium ${getPerformanceColor(partner?.satisfaction, 4.5)}`}>
                        {partner?.satisfaction}
                      </span>
                    </div>
                  </td>
                  <td className="p-4 text-center">
                    <span className="text-sm font-medium text-foreground">${partner?.avgCost}</span>
                  </td>
                  <td className="p-4 text-center">
                    <span className="text-sm text-muted-foreground">{partner?.responseTime}min</span>
                  </td>
                  <td className="p-4 text-center">
                    <Icon name={getTrendIcon(partner?.trend)} size={18} className={getTrendColor(partner?.trend)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {/* Cost Analysis */}
      <div className="glass-container rounded-xl border border-border/50">
        <div className="p-6 border-b border-border/50">
          <h3 className="text-lg font-semibold text-foreground">Cost Analysis by Category</h3>
          <p className="text-sm text-muted-foreground mt-1">Budget allocation and spending patterns</p>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {costAnalysis?.map((item, idx) => (
              <div key={idx} className="p-4 rounded-lg border border-border/30">
                <h4 className="text-sm font-semibold text-foreground mb-4">{item?.category}</h4>
                <div className="space-y-3">
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Total Spent</p>
                    <p className="text-2xl font-bold text-foreground">${(item?.spent / 1000)?.toFixed(1)}K</p>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">Bookings</span>
                    <span className="text-sm font-medium text-foreground">{item?.bookings}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">Avg Cost</span>
                    <span className="text-sm font-medium text-primary">${item?.avgCost}</span>
                  </div>
                  <div className="pt-3 border-t border-border/30">
                    <div className="w-full bg-muted/20 rounded-full h-2">
                      <div 
                        className="bg-primary rounded-full h-2" 
                        style={{ width: `${(item?.spent / 400000) * 100}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PerformanceAnalytics;